import { defineStore } from 'pinia';
import { reactive, ref, watch } from 'vue';
import type {
  BasicObserver,
  BasicSignee,
  DocumentDialogState
} from '@/types/ui';
import type { PDFReader } from '@deepcloud/pdf';

export const useGlobalStore = defineStore('global', () => {
  const currentPage = ref(1);
  const history = ref<string[]>([]);
  const pageCount = ref();
  const zoomLevel = ref(1);
  const viewportOffset = reactive({ top: 0, left: 0 });
  const pdfPreviewRendered = ref(false);
  const drawer = ref<boolean | null>(null);
  const bottomSheet = ref<boolean | null>(null);
  const documentDialog = ref<boolean | null>(null);
  const documentDialogState = ref<DocumentDialogState>(null);
  const signingWorkflowDialog = ref(false);
  const signingWorkflowStep = ref<1 | 2>(1);
  // true => full stepper wizard (first-time setup); false => single-section edit
  const signingWorkflowWizard = ref(false);
  const selectedSeal = ref(null);
  const pdfPreviewRef = ref<InstanceType<typeof PDFReader>>();
  const selectedDocumentFileId = ref<string | null>(null);
  const pdfSidebarOpen = ref(true);

  const selectedSignee = ref<BasicSignee>();

  watch(documentDialog, (value) => {
    if (!value) {
      // on dialog close we reset the store
      documentDialogState.value = null;
      selectedSignee.value = undefined;
      selectedObserver.value = undefined;
      documentDialogState.value = null;
    }
  });

  function setCurrentPage(currentPageParam: number) {
    currentPage.value = currentPageParam;
  }

  function setZoomLevel(zoomLevelParam: number) {
    zoomLevel.value = zoomLevelParam;
  }

  function setViewportOffset(viewportOffsetParam: {
    left: number;
    top: number;
  }) {
    const { left = 0, top = 0 } = viewportOffsetParam;
    viewportOffset.left = left;
    viewportOffset.top = top;
  }

  function setPageCount(pageCountParam: number | null) {
    pageCount.value = pageCountParam;
  }

  function setPdfPreviewRef(pdfPreview) {
    pdfPreviewRef.value = pdfPreview;
  }

  function setSelectedDocumentFileId(fileId: string | null) {
    selectedDocumentFileId.value = fileId;
  }

  function setPdfSidebarOpen(isOpen: boolean) {
    pdfSidebarOpen.value = isOpen;
  }

  function addRouteToHistory(route: string) {
    history.value.push(route);
  }

  // Observers
  const selectedObserver = ref<BasicObserver>();

  function setSelectedObserver(value?: BasicObserver) {
    selectedObserver.value = value;
  }

  function setSelectedSeal(value) {
    selectedSeal.value = value;
  }

  return {
    // State
    currentPage,
    pageCount,
    zoomLevel,
    viewportOffset,
    pdfPreviewRendered,
    pdfPreviewRef,
    selectedDocumentFileId,
    pdfSidebarOpen,
    history,
    drawer,
    bottomSheet,
    documentDialog,
    documentDialogState,
    signingWorkflowDialog,
    signingWorkflowStep,
    signingWorkflowWizard,
    selectedSignee,
    selectedSeal,

    // Actions
    setCurrentPage,
    setPageCount,
    setZoomLevel,
    setViewportOffset,
    setPdfPreviewRef,
    setSelectedDocumentFileId,
    setPdfSidebarOpen,
    addRouteToHistory,

    // Observers
    selectedObserver,
    setSelectedObserver,
    setSelectedSeal
  };
});
